import _ from "lodash";

// Caminhos de configuração de ação são strings separadas por ponto (ex.:
// "fields.fields.0.value") resolvidas dentro de `action.config`.
//
// Endereçar um item de lista pela posição é frágil: os campos de "Criar/Atualizar
// Registro" ficam num array cuja ordem é a de inclusão, e não a de exibição, então
// adicionar ou remover um campo faz o editor ler e gravar no campo errado. O
// segmento abaixo endereça o item pelo seu `id`, que é estável.
const ID_SEGMENT = "@id:";

export const buildIdSegment = (id) => `${ID_SEGMENT}${id}`;

// O caminho é dividido por ponto, então um id com ponto não pode ser usado como
// segmento — nesses casos quem monta o caminho deve cair no índice.
export const canUseIdSegment = (id) =>
    (typeof id === "string" || typeof id === "number") &&
    !_.toString(id).includes(".") &&
    !_.isEmpty(_.toString(id));

export const parseFieldPath = (path) => _.toString(path).split(".");

// Chave de um campo de "Criar/Atualizar Registro" dentro do formulário do antd.
//
// Pelo mesmo motivo do segmento acima, a identidade do campo é o `id`, não o
// nome: o nome é o rótulo, e muda quando o campo é renomeado no formulário de
// origem. Enquanto a ponte era feita pelo nome, renomear o campo desligava o
// valor gravado da coluna que o exibe — ele sumia da tela e, na primeira edição
// seguinte do passo, era apagado da configuração (o formulário não devolve valor
// para uma chave que não existe nele). O nome fica como reserva para os campos
// gravados antes de o `id` existir.
export const recordFieldKey = (id, name) => {
    const key = _.toString(id ?? "");
    return key !== "" ? key : _.toString(name ?? "");
};

// Traduz um segmento na chave real do nó. Retorna null quando o segmento aponta
// para um id que não existe mais na lista.
const resolveSegment = (node, segment) => {
    if (Array.isArray(node) && _.startsWith(segment, ID_SEGMENT)) {
        const id = segment.slice(ID_SEGMENT.length);
        const index = node.findIndex((item) => item && _.toString(item.id) === id);
        return index > -1 ? index : null;
    }
    return segment;
};

// Leitura pura: não cria nem altera nada no caminho percorrido.
export const getAtFieldPath = (root, path) => {
    let node = root;
    for (const segment of parseFieldPath(path)) {
        if (!_.isObject(node)) return undefined;
        const key = resolveSegment(node, segment);
        if (key === null) return undefined;
        node = node[key];
    }
    return node;
};

// Escrita imutável: devolve uma nova árvore clonando apenas os nós do caminho,
// preservando o restante por referência. Devolve a raiz intacta quando o caminho
// não pode ser resolvido.
export const setAtFieldPath = (root, path, value) => {
    const segments = parseFieldPath(path);

    const write = (node, depth) => {
        if (depth === segments.length) {
            return value;
        }
        const clone = Array.isArray(node)
            ? [...node]
            : _.isPlainObject(node)
                ? { ...node }
                : {};
        const key = resolveSegment(clone, segments[depth]);
        if (key === null) {
            return node;
        }
        clone[key] = write(clone[key], depth + 1);
        return clone;
    };

    return write(root, 0);
};
